import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, RefreshCw, GitMerge } from 'lucide-react';
import { AssistantFusionFiches } from './AssistantFusionFiches'; // 👥 E5

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const CATEGORIES = [
  ['fiches_meme_numero', 'Fiches partageant un numéro d\'employé'],
  ['fiches_meme_courriel', 'Fiches partageant un courriel'],
  ['comptes_desactives_fiche_active', 'Comptes désactivés dont une fiche est active'],
  ['fiches_actives_compte_desactive', 'Fiches actives dont le compte est désactivé'],
  ['fiches_inactives_compte_actif', 'Fiches inactives avec compte actif'],
  ['utilisateurs_sans_fiche', 'Utilisateurs sans fiche employé'],
  ['liens_employee_id_manquants', 'Comptes liés par id seulement (employee_id manquant)'],
];

const Etat = ({ ok, oui = 'active', non = 'inactive' }) => <Badge className={`text-xs ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{ok ? oui : non}</Badge>;

function Ligne({ item, onFiche, onCompte, onRegrouper }) {
  const fiches = item.fiches || [item];
  return (
    <div className="border rounded-lg p-3 space-y-2 bg-white" data-testid={`coherence-item-${item.employee_id || item.user_id || item.numero_employe || item.email}`}>
      {item.numero_employe && item.fiches && <p className="text-sm font-semibold">Numéro {item.numero_employe}</p>}
      {item.email && item.fiches && <p className="text-sm font-semibold">{item.email}</p>}
      {fiches.map((f, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{f.nom || f.email}</span>
          {f.numero_employe && !item.fiches && <span className="text-slate-500">#{f.numero_employe}</span>}
          {f.type_employe && <span className="text-slate-400 text-xs">{f.type_employe}</span>}
          {f.employee_id && <>fiche <Etat ok={f.fiche_active} /></>}
          {f.user_id && <>compte <Etat ok={f.compte_actif} oui="actif" non="désactivé" />{f.desactive_raison && <span className="text-xs text-slate-500">({f.desactive_raison})</span>}</>}
          {f.employee_id && <Button size="sm" variant="outline" className="h-7" onClick={() => onFiche(f.employee_id)} data-testid={`ouvrir-fiche-${f.employee_id}`}>Ouvrir la fiche</Button>}
          {f.user_id && <Button size="sm" variant="outline" className="h-7" onClick={() => onCompte(f.user_id)} data-testid={`ouvrir-compte-${f.user_id}`}>Ouvrir le compte</Button>}
        </div>
      ))}
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">{item.action_suggeree}</p>
      {onRegrouper && item.fiches && item.fiches.length >= 2 && (
        <Button size="sm" variant="outline" className="h-7" onClick={() => onRegrouper(item.fiches.map((f) => f.employee_id))} data-testid={`regrouper-${item.numero_employe || item.email}`}>
          <GitMerge className="w-3 h-3 mr-1" /> Regrouper
        </Button>
      )}
    </div>
  );
}

// 👥 Employés E1 : rapport « Cohérence employés & comptes » (propriétaire, lecture seule).
export function CoherenceEmployesComptes({ onOuvrirFiche, onOuvrirCompte }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouverte, setOuverte] = useState(false);
  const [fusionIds, setFusionIds] = useState(null); // 👥 E5
  const charger = () => axios.get(`${API_URL}/api/employes/coherence`).then((r) => { setData(r.data); setErreur(''); }).catch(() => setErreur('Impossible de charger le rapport de cohérence.'));
  useEffect(() => { charger(); }, []);
  const total = data ? Object.values(data.compteurs).reduce((a, b) => a + b, 0) : 0;
  const ouvrirFiche = onOuvrirFiche || ((id) => window.location.assign(`/gestion-employes?employe=${id}`));
  const ouvrirCompte = onOuvrirCompte || ((id) => window.location.assign(`/gestion-utilisateurs?user=${id}`));
  return (
    <Card data-testid="coherence-employes-comptes">
      <CardHeader className="cursor-pointer" onClick={() => setOuverte((o) => !o)} data-testid="coherence-toggle">
        <CardTitle className="flex items-center gap-2 text-base"><ShieldAlert className="w-5 h-5 text-amber-600" />Cohérence employés &amp; comptes
          <Badge className={total ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'} data-testid="coherence-total">{data ? `${total} point(s) à vérifier` : '…'}</Badge>
          <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={(e) => { e.stopPropagation(); charger(); }} data-testid="coherence-rafraichir"><RefreshCw className="w-3 h-3" /></Button>
        </CardTitle>
        <CardDescription>Lecture seule : aucune correction automatique. Règle : un identifiant reste fonctionnel tant qu'au moins une fiche de la personne est active.</CardDescription>
      </CardHeader>
      {(ouverte || total > 0) && data && (
        <CardContent className="space-y-4">
          {erreur && <p className="text-sm text-red-600" data-testid="coherence-erreur">{erreur}</p>}
          {CATEGORIES.map(([cle, titre]) => (
            <div key={cle} data-testid={`coherence-categorie-${cle}`}>
              <p className="text-sm font-semibold flex items-center gap-2">{titre} <Badge variant="outline" data-testid={`coherence-compteur-${cle}`}>{data.compteurs[cle]}</Badge></p>
              {data[cle].length > 0 && <div className="mt-2 space-y-2">{data[cle].map((it, i) => <Ligne key={i} item={it} onFiche={ouvrirFiche} onCompte={ouvrirCompte} onRegrouper={(cle === 'fiches_meme_numero' || cle === 'fiches_meme_courriel') ? setFusionIds : null} />)}</div>}
            </div>
          ))}
          {/* 👥 Migrations au démarrage : écart détecté → non appliquée, visible ici (propriétaire) */}
          <div data-testid="coherence-categorie-migrations_en_attente">
            <p className="text-sm font-semibold flex items-center gap-2">Migration en attente — écart détecté <Badge variant="outline" data-testid="coherence-compteur-migrations_en_attente">{data.compteurs.migrations_en_attente ?? 0}</Badge></p>
            {(data.migrations_en_attente || []).map((m) => (
              <div key={m.nom} className="mt-2 border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs space-y-1" data-testid={`migration-ecart-${m.nom}`}>
                <p className="font-semibold text-amber-900">{m.nom} v{m.version} · {m.date ? new Date(m.date).toLocaleString('fr-CA') : ''}</p>
                <p className="text-amber-800">{m.action_suggeree}</p>
                <ul className="list-disc pl-4 text-slate-700">{(m.ecarts || []).map((e, i) => <li key={i}>{Object.entries(e).map(([k, v]) => `${k} : ${String(v)}`).join(' · ')}</li>)}</ul>
              </div>
            ))}
          </div>
        </CardContent>
      )}
      <AssistantFusionFiches ids={fusionIds || []} ouvert={!!fusionIds} onFermer={() => setFusionIds(null)} onTermine={() => charger()} />
    </Card>
  );
}
